import React, { useCallback, useEffect, useState } from "react";
import {
    Page,
    Card,
    BlockStack,
    InlineGrid,
    InlineStack,
    Box,
    Text,
    Select,
    Spinner,
    DataTable,
    Badge,
    EmptyState,
    Divider,
    Banner,
    Button,
    TextField,
} from "@shopify/polaris";
import { useSelector } from "react-redux";
import { GetApiCall } from "../../helper/axios";
import ReviewBanner from "../Review/ReviewBanner";
import { BarChart, DoughnutChart, CHART_PALETTE } from "./Charts";
import SalesByCurrency from "./SalesByCurrency";
import RecommendedApps from "../AppSuite/RecommendedApps";

const RANGE_OPTIONS = [
    { label: "Last 7 days", value: "7" },
    { label: "Last 30 days", value: "30" },
    { label: "Last 90 days", value: "90" },
    { label: "Custom range", value: "custom" },
];
const TODAY_STR = new Date().toISOString().slice(0, 10);

/* --------------------------------- pieces -------------------------------- */
function DeltaBadge({ pct }) {
    if (pct === null || pct === undefined) return null;
    if (pct > 0) return <Badge tone="success">{`+${pct}%`}</Badge>;
    if (pct < 0) return <Badge tone="critical">{`${pct}%`}</Badge>;
    return <Badge tone="info">0%</Badge>;
}

function StatCard({ label, value, badge, sub }) {
    return (
        <Card>
            <BlockStack gap="100">
                <Text as="span" variant="bodySm" tone="subdued">{label}</Text>
                <InlineStack gap="200" blockAlign="center" wrap={false}>
                    <Text as="p" variant="heading2xl">{value}</Text>
                    {badge || null}
                </InlineStack>
                {sub ? <Text as="span" variant="bodySm" tone="subdued">{sub}</Text> : null}
            </BlockStack>
        </Card>
    );
}

function ShareBar({ pct }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 130 }}>
            <div style={{ flex: 1, height: 6, background: "#edeef0", borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: `${Math.min(100, pct)}%`, height: "100%", background: CHART_PALETTE[0], borderRadius: 3 }} />
            </div>
            <span style={{ fontVariantNumeric: "tabular-nums", minWidth: 42, textAlign: "right", fontSize: 13 }}>{pct}%</span>
        </div>
    );
}

function ChartCard({ title, subtitle, hasContent, emptyText, children }) {
    return (
        <Card>
            <BlockStack gap="300">
                <BlockStack gap="050">
                    <Text as="h2" variant="headingMd">{title}</Text>
                    {subtitle ? <Text as="span" variant="bodySm" tone="subdued">{subtitle}</Text> : null}
                </BlockStack>
                <Divider />
                {hasContent ? children : <Text as="span" tone="subdued">{emptyText}</Text>}
            </BlockStack>
        </Card>
    );
}

function AnalyticsDashboard({ embedded = false, onOpenFeedback, reviewRefresh = 0 }) {
    const { store_data } = useSelector((store) => store.commonData);
    const token = store_data?.token;

    const [range, setRange] = useState("30");
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [appliedCustom, setAppliedCustom] = useState(null); // {from,to} once Apply is pressed
    const [overview, setOverview] = useState(null);
    const [trend, setTrend] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchAll = useCallback(async (qs) => {
        setLoading(true);
        try {
            const [ov, tr] = await Promise.all([
                GetApiCall("GET", `/analytics/overview?${qs}`, { authentication: token }),
                GetApiCall("GET", `/analytics/trend?${qs}`, { authentication: token }),
            ]);
            if (ov?.data?.statusCode === 200 && ov?.data?.data) setOverview(ov.data.data);
            if (tr?.data?.statusCode === 200 && tr?.data?.data) setTrend(tr.data.data);
        } catch (e) {
            console.log("analytics fetch error", e);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (!token) return;
        if (range === "custom") {
            if (appliedCustom) fetchAll(`from=${appliedCustom.from}&to=${appliedCustom.to}`);
        } else {
            fetchAll(`range=${range}`);
        }
    }, [token, range, appliedCustom, fetchAll]);

    const applyCustom = () => {
        if (customFrom && customTo && customFrom <= customTo) {
            setAppliedCustom({ from: customFrom, to: customTo });
        }
    };

    // Heading-row controls: range select + (when "Custom range") date pickers.
    const filterControls = (
        <InlineStack gap="200" blockAlign="center" wrap>
            <div style={{ minWidth: 160 }}>
                <Select label="Range" labelHidden options={RANGE_OPTIONS} value={range} onChange={setRange} />
            </div>
            {range === "custom" ? (
                <>
                    <div style={{ width: 150 }}>
                        <TextField label="From" labelHidden type="date" value={customFrom} max={customTo || TODAY_STR} onChange={setCustomFrom} autoComplete="off" />
                    </div>
                    <div style={{ width: 150 }}>
                        <TextField label="To" labelHidden type="date" value={customTo} min={customFrom} max={TODAY_STR} onChange={setCustomTo} autoComplete="off" />
                    </div>
                    <Button onClick={applyCustom} disabled={!customFrom || !customTo || customFrom > customTo}>Apply</Button>
                </>
            ) : null}
        </InlineStack>
    );

    const reviewBanner = <ReviewBanner onOpenFeedback={onOpenFeedback} refreshSignal={reviewRefresh} />;

    const wrap = (children, headerRight) =>
        embedded ? (
            <div className="mt-20">
                {reviewBanner}
                <BlockStack gap="400">
                    <InlineStack align="space-between" blockAlign="center" gap="400" wrap>
                        <Text as="h1" variant="headingLg">Analytics</Text>
                        {headerRight || null}
                    </InlineStack>
                    {children}
                </BlockStack>
            </div>
        ) : (
            <Page title="Analytics" subtitle="Understand how shoppers interact with currencies">
                {reviewBanner}
                {headerRight ? <InlineStack align="end">{headerRight}</InlineStack> : null}
                {children}
            </Page>
        );

    if (loading && !overview) {
        return wrap(
            <div style={{ textAlign: "center", paddingTop: embedded ? "10%" : "15%" }}>
                <Spinner accessibilityLabel="Loading analytics" size="large" />
            </div>
        );
    }

    const hasData =
        overview &&
        ((overview.views?.last30 || 0) + (overview.switches?.last30 || 0) > 0 ||
            (overview.topCurrencies && overview.topCurrencies.length > 0));

    if (!hasData) {
        return wrap(
            <Card>
                <EmptyState
                    heading="No analytics yet"
                    image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                    <p>
                        As shoppers view prices and switch currencies on your storefront, this
                        dashboard will fill with engagement insights. Make sure the currency
                        converter app block is enabled in your theme.
                    </p>
                </EmptyState>
            </Card>
        );
    }

    const sw = overview.switches || { today: 0, last7: 0, last30: 0 };
    const vw = overview.views || { today: 0, last7: 0, last30: 0 };
    const rc = overview.rangeComparison || {
        switches: { cur: sw.last30, deltaPct: null },
        views: { cur: vw.last30, deltaPct: null },
    };
    const top = overview.topCurrencies || [];
    const dist = overview.distribution || [];
    const coverage = overview.coverage || { enabled: 0, supported: 164 };
    const mostPopular = overview.mostPopular;
    const reach = overview.shopperReach || 0;
    const rangeLabel = range === "custom" ? "period" : `${range}d`;
    const salesQuery = range === "custom" ? (appliedCustom ? `from=${appliedCustom.from}&to=${appliedCustom.to}` : "") : `range=${range}`;

    const topRows = top.map((c) => [c.currency, c.views.toLocaleString(), <ShareBar key={c.currency} pct={c.percentage} />]);

    // Engagement rate: how many switches happen per 100 Total Visits.
    const engagementRate = (rc.views.cur || 0) > 0 ? Math.round((rc.switches.cur / rc.views.cur) * 1000) / 10 : 0;
    // Peak day: the busiest day in the selected range (by total activity).
    const peak = (trend && trend.series ? trend.series : []).reduce((best, d) => {
        const tot = (d.switches || 0) + (d.views || 0);
        return tot > (best ? best.tot : -1) ? { date: d.date, tot, switches: d.switches, views: d.views } : best;
    }, null);
    const peakText = peak && peak.tot > 0
        ? `Peak ${peak.date.slice(5)}: ${peak.views.toLocaleString()} views, ${peak.switches.toLocaleString()} switches`
        : null;

    const insight = mostPopular
        ? `${mostPopular.currency} drives ${mostPopular.percentage}% of all currency views. Shoppers browsed in ${reach} ${reach === 1 ? "currency" : "currencies"} over the last 30 days, and you've enabled ${coverage.enabled} of ${coverage.supported} supported currencies.`
        : null;

    return wrap(
        <BlockStack gap="400">
            {insight ? <Banner tone="info">{insight}</Banner> : null}

            <InlineGrid columns={{ xs: 1, sm: 2, md: 3, lg: 5 }} gap="400">
                <StatCard
                    label="Currency switches"
                    value={(rc.switches.cur ?? sw.last30).toLocaleString()}
                    badge={<DeltaBadge pct={rc.switches.deltaPct} />}
                    sub={`vs prev ${rangeLabel} · Today ${sw.today.toLocaleString()}`}
                />
                <StatCard
                    label="Total Visits"
                    value={(rc.views.cur ?? vw.last30).toLocaleString()}
                    badge={<DeltaBadge pct={rc.views.deltaPct} />}
                    sub={`vs prev ${rangeLabel} · Today ${vw.today.toLocaleString()}`}
                />
                <StatCard
                    label="Engagement rate"
                    value={`${engagementRate}`}
                    sub="switches per 100 views"
                />
                <StatCard
                    label="Most popular currency"
                    value={mostPopular ? mostPopular.currency : "—"}
                    badge={mostPopular ? <Badge tone="success">{`${mostPopular.percentage}%`}</Badge> : null}
                    sub={mostPopular ? "of all views" : "No views yet"}
                />
                <StatCard
                    label="Shopper reach"
                    value={`${reach}`}
                    sub="currencies viewed (30d)"
                />
            </InlineGrid>

            <InlineGrid columns={{ xs: 1, md: ["twoThirds", "oneThird"] }} gap="400">
                <ChartCard
                    title="Daily switches & views"
                    subtitle={peakText}
                    hasContent={!!(trend && trend.series && trend.series.length)}
                    emptyText="No trend data for this range."
                >
                    {trend && trend.series ? (
                        <BarChart
                            labels={trend.series.map((d) => d.date.slice(5))}
                            switches={trend.series.map((d) => d.switches)}
                            views={trend.series.map((d) => d.views)}
                        />
                    ) : null}
                </ChartCard>
                <SalesByCurrency token={token} query={salesQuery} />
            </InlineGrid>

            <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
                <ChartCard title="Shopper currency distribution" hasContent={dist.length > 0} emptyText="No views yet.">
                    <DoughnutChart labels={dist.map((d) => d.label)} data={dist.map((d) => d.value)} />
                </ChartCard>
                <ChartCard title="Top 10 viewed currencies" hasContent={topRows.length > 0} emptyText="No views yet.">
                    <DataTable
                        columnContentTypes={["text", "numeric", "text"]}
                        headings={["Currency", "Views", "Share"]}
                        rows={topRows}
                    />
                </ChartCard>
            </InlineGrid>

            <Divider />

            <RecommendedApps />

            <Box paddingBlockEnd="400">
                <Text as="span" variant="bodySm" tone="subdued">
                    Privacy-friendly: only aggregated event counts are stored. No personal
                    shopper data, IP addresses, or session identifiers are collected.
                </Text>
            </Box>
        </BlockStack>,
        filterControls
    );
}

export default AnalyticsDashboard;
